import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Badge,
  Input,
  Label,
  Form
} from "reactstrap";
import { useHistory } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import ApiClient from "../BaseUrl/ApiClient";
import "./PlanSelectionModal.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Stripe Payment Form Component
function StripePaymentForm({ clientSecret, selectedPlan, isUpgrade, onSuccess, onError, modalConfig = {} }) {
  const [cardholder, setCardholder] = useState("");
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Handle change plan with existing card (skip Stripe setup)
    if (modalConfig.action === 'changePlan' && modalConfig.skipCardDetails) {
      setLoading(true);
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const payload = { planType: selectedPlan.id };

        const response = await ApiClient().put("/business/payment/changePlan", payload);
        const { responseData } = response.data;

        // Update subscriptionType in localStorage
        const subscriptionType = responseData?.businessProfile?.subscriptionType || selectedPlan?.name?.toLowerCase() || "free";
        localStorage.setItem("subscriptionType", subscriptionType);

        toast.success("Plan changed successfully!");

        // Dispatch custom event to update sidebar subscription status
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));

        onSuccess && onSuccess();
        setLoading(false);
        return;
      } catch (error) {
        toast.error(error?.response?.data?.responseMessage?.[0] || "Failed to change plan");
        setLoading(false);
        return;
      }
    }

    // Handle Stripe payment flow
    if (!stripe || !elements) {
      toast.error("Stripe not initialized");
      return;
    }
    setLoading(true);
    try {
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });
      if (error) {
        toast.error(error.message);
        setLoading(false);
        return;
      }
      if (setupIntent?.status === "succeeded") {
        const authUser = JSON.parse(localStorage.getItem("authUser"));

        // Determine API endpoint and method based on action
        let apiEndpoint, apiMethod, payload;

        if (modalConfig.action === 'changePlan') {
          // Change plan API
          apiEndpoint = "/business/payment/changePlan";
          apiMethod = "put";

          // Start with planType
          payload = { planType: selectedPlan.id };

          // If no payment card, we need to include payment details
          if (!modalConfig.skipCardDetails) {
            payload.stripeCustomerId = authUser?.stripeCustomerId || "68779fe49b6750e94186ad19";
            payload.stripePaymentMethodId = setupIntent.payment_method;
          }
        } else if (modalConfig.action === 'addCard' || modalConfig.action === 'updateCard') {
          // Add/Update card API
          apiEndpoint = "/business/payment/card";
          apiMethod = "put";
          payload = {
            stripeCustomerId: authUser?.stripeCustomerId || "68779fe49b6750e94186ad19",
            stripePaymentMethodId: setupIntent.payment_method,
            planType: selectedPlan.id,
          };
        } else {
          // Default flow - use the correct endpoint that exists
          apiEndpoint = "/business/payment/card";
          apiMethod = isUpgrade ? "put" : "post";
          payload = {
            stripeCustomerId: authUser?.stripeCustomerId || "68779fe49b6750e94186ad19",
            stripePaymentMethodId: setupIntent.payment_method,
            planType: selectedPlan.id,
          };
        }

        console.log('API Call Details:', {
          action: modalConfig.action,
          skipCardDetails: modalConfig.skipCardDetails,
          apiEndpoint,
          apiMethod,
          payload
        });

        ApiClient()[apiMethod](apiEndpoint, payload)
          .then((response) => {
            const { responseData } = response.data;
            const { subscriptionInfo, data, businessProfile } = responseData;

            if (data && data.user.roleType !== 1) {
              throw new Error("Access denied. Only business users can log in.");
            }

            if (data) {
              localStorage.setItem("accessToken", data.accessToken);
              localStorage.setItem("refreshToken", data.refreshToken);
              localStorage.setItem("authUser", JSON.stringify(data.user));
            }

            // Update subscriptionType in localStorage
            const subscriptionType = businessProfile?.subscriptionType || selectedPlan?.name?.toLowerCase() || "free";
            localStorage.setItem("subscriptionType", subscriptionType);

            toast.success(isUpgrade ? "Subscription updated successfully!" : "Successfully subscribed!");

            // Dispatch custom event to update sidebar subscription status
            window.dispatchEvent(new CustomEvent('subscriptionUpdated'));

            onSuccess();
          })
          .catch((error) => {
            console.error('API Error:', error);
            console.error('Error Response:', error?.response?.data);
            const messages = error?.response?.data?.responseMessage || [isUpgrade ? "Update failed" : "Payment failed"];
            messages.forEach((msg) => toast.error(msg));
            onError();
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        toast.error("Setup failed. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      toast.error("Unexpected error occurred.");
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mt-3">
        <Col md={12}>
          <Label>Cardholder name</Label>
          <Input
            type="text"
            value={cardholder}
            placeholder="Enter your name"
            onChange={(e) => setCardholder(e.target.value)}
            required
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col md={12}>
          <Label>Payment Details</Label>
          <div className="card-input">
            <PaymentElement options={{ layout: "tabs" }} />
          </div>
        </Col>
      </Row>
      <Button
        type="submit"
        disabled={!stripe || loading}
        className="mt-4 signin-btn w-100"
      >
        {loading ? "Processing..." :
          (modalConfig.action === 'addCard' ? "Add Card" :
            modalConfig.action === 'updateCard' ? "Update Card" :
              modalConfig.action === 'changePlan' ? "Change Plan" :
                isUpgrade ? "Update Subscription" : "Subscribe")}{" "}
        <i className="ms-2 fa-solid fa-arrow-right" />
      </Button>
    </Form>
  );
}

const PlanSelectionModal = ({ isOpen, toggle, isUpgrade = false, onSubscriptionSuccess, modalConfig = {} }) => {
  const [currentStep, setCurrentStep] = useState(1); // 1: Plan Selection, 2: Payment
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentActivePlan, setCurrentActivePlan] = useState(null);
  const history = useHistory();

  const plans = [
    {
      id: 1,
      name: "Standard",
      price: 20,
      features: [
        { text: "Daily Special: Up to 10 posts, Unlimited days", bold: true },
        { text: "Lunch Special : Up to 4 posts, Unlimited days", bold: true },
        { text: "Happy Hours : Up to 5 posts, Unlimited days", bold: true }
      ],
      color: "primary"
    },
    {
      id: 2,
      name: "Sponsored",
      price: 100,
      features: [
        { text: "All features of Standard", bold: false },
        { text: "Top visibility in your region", bold: true }
      ],
      color: "success"
    }
  ];

  // Handle modal config to determine initial step and selected plan
  useEffect(() => {
    if (isOpen && modalConfig.skipPlanSelection) {
      // Skip plan selection and go directly to payment step
      setCurrentStep(2);
      // Find the full plan object from the plans array
      const fullPlan = plans.find(plan => plan.id === modalConfig.selectedPlan);
      setSelectedPlan(fullPlan || modalConfig.selectedPlan);
    } else if (isOpen) {
      // Normal flow - start with plan selection
      setCurrentStep(1);
      setSelectedPlan(null);
    }
  }, [isOpen, modalConfig]);

  // Fetch current subscription plan when modal opens for upgrades
  useEffect(() => {
    if (isOpen && isUpgrade) {
      fetchCurrentSubscription();
    }
  }, [isOpen, isUpgrade]);

  // Fetch client secret when moving to payment step
  useEffect(() => {
    if (currentStep === 2 && selectedPlan) {
      fetchClientSecret();
    }
  }, [currentStep, selectedPlan]);

  const fetchCurrentSubscription = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await ApiClient(token).get("/business/payment/subscription");
      const responseData = response.data?.responseData;

      if (responseData) {
        // Use subscriptionType from API response instead of subscriptionStatus
        const subscriptionType = responseData.subscriptionType;
        if (subscriptionType) {
          // Map subscription type to plan ID
          const planMapping = {
            'standard': 1,
            'sponsored': 2
          };
          setCurrentActivePlan(planMapping[subscriptionType] || null);
        }
      }
    } catch (error) {
      console.error("Failed to fetch current subscription:", error);
      // If API fails, don't set current plan
    }
  };

  const fetchClientSecret = async () => {
    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const customerId = authUser?.stripeCustomerId || "68779fe49b6750e94186ad19";
      const token = localStorage.getItem("accessToken");
      const { data } = await ApiClient(token).post(
        "/business/payment/setupIntent",
        { stripeCustomerId: customerId }
      );
      setClientSecret(data.responseData.setupIntent.client_secret);
    } catch (error) {
      toast.error("Failed to initialize Stripe");
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    // Don't allow selection of current active plan in upgrade mode
    if (isUpgrade && currentActivePlan && plan.id === currentActivePlan) {
      return;
    }
    setSelectedPlan(plan);
  };

  const handleContinue = () => {
    if (!selectedPlan) return;
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSuccess = () => {
    // Close modal immediately to prevent double display
    handleClose();

    // Trigger refresh of subscription data in parent component
    if (onSubscriptionSuccess) {
      onSubscriptionSuccess();
    }

    setTimeout(() => {
      history.push("/business-profile");
    }, 1500);
  };

  const handleError = () => {
    // Error handling is done in the payment form
  };

  const handleClose = () => {
    setSelectedPlan(null);
    setCurrentStep(1);
    setClientSecret(null);
    setCurrentActivePlan(null);
    toggle();
  };

  return (
    <div className="plan-selection-modal-wrapper">
      <ToastContainer />
      <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered className="plan-selection-modal">
        <ModalHeader className="p-4 d-flex align-items-center justify-content-center position-relative">
          <h5 className="m-0 text-center text-dark flex-grow-1">
            {currentStep === 1
              ? (modalConfig.action === 'changePlan' ? "Change Your Plan" :
                modalConfig.action === 'addCard' ? "Add Payment Card" :
                  modalConfig.action === 'updateCard' ? "Update Payment Card" :
                    isUpgrade ? "Upgrade Your Plan" : "Choose Your Plan")
              : (modalConfig.action === 'addCard' ? "Add Payment Card" :
                modalConfig.action === 'updateCard' ? "Update Payment Card" :
                  modalConfig.action === 'changePlan' ? "Complete Plan Change" :
                    isUpgrade ? "Update Payment" : "Complete Payment")
            }
          </h5>

          <button
            type="button"
            className="btn-close position-absolute top-50 translate-middle-y end-0 me-3"
            onClick={handleClose}
            aria-label="Close"
          ></button>
        </ModalHeader>



        <ModalBody>
          {/* Step Indicator */}
          <div className="modal-step-indicator">
            <div className={`step-dot ${currentStep >= 1 ? 'active' : ''}`}></div>
            <div className={`step-dot ${currentStep >= 2 ? 'active' : ''}`}></div>
          </div>

          {currentStep === 1 ? (
            // Step 1: Plan Selection
            <>
              <div className="text-center mb-4">
                <p className="text-muted">
                  {isUpgrade
                    ? "Select a plan to upgrade your current subscription"
                    : "Choose the perfect plan for your business needs"
                  }
                </p>
              </div>
              {/* 
              <div className="text-center mb-4 p-4 rounded shadow-sm bg-light">
                <p className="fs-4 fw-bold">
                  E-TRANSFER AVAILABLE
                </p>
                <p className="fs-6">
                  To pay by <strong>e-transfer</strong>, please email us at 
                  <a href="mailto:contact@findagram.ca" className="fw-semibold text-decoration-none">
                    {" "}contact@findagram.ca
                  </a>{" "}and we will provide you with the payment details immediately.
                </p>
              </div> */}

              <Row className="g-4 justify-content-center">
                {plans.map((plan) => {
                  const isCurrentPlan = isUpgrade && currentActivePlan && plan.id === currentActivePlan;
                  const isSelected = selectedPlan?.id === plan.id;
                  const isDisabled = isCurrentPlan;

                  return (
                    <Col md={4} key={plan.id}>
                      <Card
                        className={`h-100 plan-card ${isSelected ? 'border-primary shadow' : ''} ${isDisabled ? 'current-plan' : ''}`}
                        style={{
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          border: isSelected ? '2px solid #007bff' : isDisabled ? '2px solid #28a745' : '1px solid #dee2e6',
                          transition: 'all 0.3s ease',
                          opacity: isDisabled ? 0.7 : 1
                        }}
                        onClick={() => !isDisabled && handlePlanSelect(plan)}
                      >
                        <CardBody className="text-center p-4">
                          <div className="mb-3">
                            <Badge color={isDisabled ? "success" : plan.color} className="px-3 py-2 fs-6">
                              {isDisabled ? plan.name : plan.name}
                            </Badge>
                          </div>

                          <CardTitle tag="h4" className="mb-2">
                            ${plan.price}
                            <small className="text-muted">/month</small>
                          </CardTitle>

                          <CardText className="text-muted mb-3">
                            <ul className="text-start ps-3 mb-0">
                              {plan.features.map((feature, index) => (
                                <li key={index} className="mb-1">
                                  {feature.bold ? (
                                    <strong>{feature.text}</strong>
                                  ) : (
                                    feature.text
                                  )}
                                </li>
                              ))}
                            </ul>
                          </CardText>

                          {isSelected && !isDisabled && (
                            <div className="mt-3">
                              <Badge color="primary" className="px-3 py-2">
                                <i className="fas fa-check me-1"></i>
                                Selected
                              </Badge>
                            </div>
                          )}

                          {isDisabled && (
                            <div className="mt-3">
                              <Badge color="success" className="border border-success text-success px-3 py-2 bg-transparent">
                                <i className="fas fa-check-circle me-1 text-success"></i>
                                Current Plan
                              </Badge>
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          ) : (
            // Step 2: Payment Form
            <>
              <div className="payment-summary text-center mb-4">
                <h6>Subscription Summary</h6>
                <p className="mb-1">
                  Plan: <span className="plan-name">{selectedPlan?.name}</span>
                </p>
                <p className="mb-0">
                  Price: <span className="plan-price">${selectedPlan?.price}/month</span>
                </p>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading payment form...</p>
                </div>
              ) : modalConfig.action === 'changePlan' && modalConfig.skipCardDetails ? (
                // Skip card details for change plan with existing card
                <div className="text-center py-4">
                  <div className="mb-4">
                    <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="mb-3">Confirm Plan Change</h5>
                  <p className="text-muted mb-4">
                    You already have a payment method on file. Your plan will be changed to <strong>{selectedPlan?.name}</strong> and your existing payment method will be used.
                  </p>
                  <Button
                    color="primary"
                    onClick={() => {
                      // Handle the change plan directly
                      const handleChangePlan = async () => {
                        setLoading(true);
                        try {
                          const payload = { planType: selectedPlan.id };
                          const response = await ApiClient().put("/business/payment/changePlan", payload);
                          const { responseData } = response.data;

                          // Update subscriptionType in localStorage
                          const subscriptionType = responseData?.businessProfile?.subscriptionType || selectedPlan?.name?.toLowerCase() || "free";
                          localStorage.setItem("subscriptionType", subscriptionType);

                          toast.success("Plan changed successfully!");

                          // Dispatch custom event to update sidebar subscription status
                          window.dispatchEvent(new CustomEvent('subscriptionUpdated'));

                          handleSuccess();
                        } catch (error) {
                          toast.error(error?.response?.data?.responseMessage?.[0] || "Failed to change plan");
                        } finally {
                          setLoading(false);
                        }
                      };
                      handleChangePlan();
                    }}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? "Processing..." : "Confirm Plan Change"}
                  </Button>
                </div>
              ) : clientSecret ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripePaymentForm
                    clientSecret={clientSecret}
                    selectedPlan={selectedPlan}
                    isUpgrade={isUpgrade}
                    onSuccess={handleSuccess}
                    onError={handleError}
                    modalConfig={modalConfig}
                  />
                </Elements>
              ) : (
                <div className="text-center py-4">
                  <p className="text-danger">Failed to load payment form. Please try again.</p>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {currentStep === 1 ? (
            // Step 1 Footer
            <>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleContinue}
                disabled={!selectedPlan}
              >
                Continue - {selectedPlan ? `$${selectedPlan.price}/month` : ""}
              </Button>
            </>
          ) : (
            // Step 2 Footer
            <>
              <Button color="secondary" onClick={handleBack}>
                Back to Plans
              </Button>
              <Button color="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PlanSelectionModal;